import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TOAST_TYPES } from '../utils/toastNotifications';
import { useToast } from '../hooks/useToast';
import { trackEvent } from '../utils/analytics';

/**
 * 🔔 Professional Toast Component for Main-Bet
 * 
 * Features:
 * - Beautiful minimalist design matching site style
 * - Smooth animations with Framer Motion
 * - Click-to-action functionality
 * - Smart positioning and responsive design
 * - Auto-dismiss with manual controls
 * - Production-ready with error boundaries
 */

const Toast = () => {
  const { currentToast, isVisible, dismiss } = useToast();

  // Safety checks
  if (!currentToast || typeof currentToast !== 'object') return null;
  if (!currentToast.type || typeof currentToast.type !== 'string') return null;

  const toastConfig = TOAST_TYPES[currentToast.type.toUpperCase()];
  if (!toastConfig) return null;

  // Color variants for different toast types - темные оттенки для лучшей гармонии
  const colorClasses = {
    primary: {
      bg: 'bg-gradient-to-r from-slate-700 to-slate-800',
      border: 'border-slate-600/50',
      text: 'text-white',
      button: 'bg-white/15 hover:bg-white/25 text-white border border-white/20'
    },
    success: {
      bg: 'bg-gradient-to-r from-emerald-700 to-emerald-800',
      border: 'border-emerald-600/50',
      text: 'text-white',
      button: 'bg-white/15 hover:bg-white/25 text-white border border-white/20'
    },
    warning: {
      bg: 'bg-gradient-to-r from-amber-700 to-orange-700',
      border: 'border-amber-600/50',
      text: 'text-white',
      button: 'bg-white/15 hover:bg-white/25 text-white border border-white/20'
    },
    info: {
      bg: 'bg-gradient-to-r from-indigo-700 to-purple-700',
      border: 'border-indigo-600/50',
      text: 'text-white',
      button: 'bg-white/15 hover:bg-white/25 text-white border border-white/20'
    }
  };

  const colors = colorClasses[toastConfig.color] || colorClasses.primary;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -100, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -100, scale: 0.95 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 25,
            duration: 0.3
          }}
          className="fixed top-4 right-4 z-50 w-full max-w-xs sm:max-w-sm px-4 sm:px-0"
          style={{ zIndex: 9999 }}
        >
          {/* Toast Container */}
          <div className={`
            ${colors.bg} ${colors.text} ${colors.border}
            rounded-xl sm:rounded-2xl shadow-2xl border backdrop-blur-sm
            p-3 sm:p-4 relative overflow-hidden
            transform transition-all duration-300
            hover:shadow-3xl hover:scale-[1.01] sm:hover:scale-[1.02]
            w-full
          `}>
            
            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-white/5 rounded-xl sm:rounded-2xl"></div>
            </div>

            {/* Close Button */}
            <button
              onClick={dismiss}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 transition-colors z-10"
              aria-label="Dismiss notification"
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Main Content */}
            <div className="relative">
              {/* Title */}
              <h3 className="font-bold text-sm sm:text-lg mb-1 sm:mb-2 pr-6 leading-tight">
                {currentToast.title}
              </h3>

              {/* Message */}
              <p className="text-xs sm:text-sm opacity-90 mb-2 sm:mb-4 leading-relaxed pr-2">
                {currentToast.message}
              </p>

              {/* Action Button */}
              {currentToast.action && (
                <div className="flex justify-end">
                  {currentToast.action.url ? (
                    <Link
                      to={currentToast.action.url}
                      onClick={() => {
                        trackEvent('click-toast');
                        dismiss();
                      }}
                      className={`
                        ${colors.button}
                        px-2 py-1 sm:px-4 sm:py-2 rounded-md sm:rounded-lg font-medium text-xs sm:text-sm
                        transition-all duration-200
                        hover:scale-105 active:scale-95
                        focus:outline-none focus:ring-2 focus:ring-white/50
                        inline-flex items-center gap-1 sm:gap-2
                      `}
                    >
                      <span className="truncate">{currentToast.action.text}</span>
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  ) : (
                    <button
                      onClick={() => {
                        trackEvent('click-toast');
                        if (currentToast.action.onClick) {
                          currentToast.action.onClick();
                        }
                        dismiss();
                      }}
                      className={`
                        ${colors.button}
                        px-2 py-1 sm:px-4 sm:py-2 rounded-md sm:rounded-lg font-medium text-xs sm:text-sm
                        transition-all duration-200
                        hover:scale-105 active:scale-95
                        focus:outline-none focus:ring-2 focus:ring-white/50
                        truncate
                      `}
                    >
                      {currentToast.action.text}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Progress Bar */}
            <motion.div
              className="absolute bottom-0 left-0 h-0.5 sm:h-1 bg-white/30 rounded-b-xl sm:rounded-b-2xl"
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ 
                duration: toastConfig.duration / 1000,
                ease: "linear"
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast; 